import { Grid, Typography } from '@mui/material';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import ImageResize from 'quill-image-resize-module-react';
import { notify } from '~/utils/helper';
import { useTranslation } from 'react-i18next';
import styles from './BaseRichEditor.module.scss';
import classNames from 'classnames/bind';
Quill.register('modules/imageResize', ImageResize);

type CreateNewProps = {
  onCreateNew: (value: string) => void;
  valueProps?: string;
  isError?: boolean;
  errorMessage?: string;
};

const cx = classNames.bind(styles);

const BaseRichEditor: React.FC<CreateNewProps> = ({ onCreateNew, isError, errorMessage, valueProps }) => {
  const { t } = useTranslation('admin/common');
  const [value, setValue] = useState<string>('');
  const reactQuillRef = useRef<ReactQuill>(null);

  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      if (input.files && input.files[0]) {
        const file = input.files[0];
        const quill = reactQuillRef.current?.getEditor();
        if (quill) {
          const reader = new FileReader();
          reader.readAsDataURL(file);

          reader.onload = () => {
            const image = reader.result;
            const range = quill.getSelection();
            if (range && image) {
              quill.insertEmbed(range.index, 'image', image);
            }
          };

          reader.onerror = (error) => {
            console.error('Error uploading image: ', error);
            notify('error', t('upload_error.text', { ns: 'admin/common' }));
          };
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
          ['image', 'link'],
          [
            {
              color: [
                '#000000',
                '#e60000',
                '#ff9900',
                '#ffff00',
                '#008a00',
                '#0066cc',
                '#9933ff',
                '#ffffff',
                '#facccc',
                '#ffebcc',
                '#ffffcc',
                '#cce8cc',
                '#cce0f5',
                '#ebd6ff',
                '#bbbbbb',
                '#f06666',
                '#ffc266',
                '#ffff66',
                '#66b966',
                '#66a3e0',
                '#c285ff',
                '#888888',
                '#a10000',
                '#b26b00',
                '#b2b200',
                '#006100',
                '#0047b2',
                '#6b24b2',
                '#444444',
                '#5c0000',
                '#663d00',
                '#666600',
                '#003700',
                '#002966',
                '#3d1466',
              ],
            },
          ],
        ],
        handlers: {
          image: imageHandler,
        },
      },
      imageResize: {
        parchment: Quill.import('parchment'),
        modules: ['Resize', 'DisplaySize'],
      },
    }),
    [imageHandler]
  );

  const handleChange = useCallback(
    (content: string) => {
      setValue(content);
      onCreateNew(content);
    },
    [onCreateNew]
  );

  useEffect(() => {
    if (valueProps) {
      setValue(valueProps);
    }
  }, [valueProps]);

  return (
    <Grid className='w-full'>
      <ReactQuill
        ref={reactQuillRef}
        theme='snow'
        modules={modules}
        value={value}
        onChange={handleChange}
        className={cx('quill', { 'quill-error': isError })}
        style={isError ? { borderColor: 'red' } : undefined}
      />
      {isError && errorMessage && <Typography className={cx('error-text')}>{errorMessage}</Typography>}
    </Grid>
  );
};

export default memo(BaseRichEditor);
